import React, { useState, useRef, useCallback } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { useAuthStore } from '../../store/authStore';
import { FiFile, FiDownload, FiMusic, FiCornerUpLeft, FiClock, FiCheck, FiAlertCircle } from 'react-icons/fi';
import './Message.css';

const SWIPE_THRESHOLD = 60;

function Message({ message, isOwn, showAvatar, onReply, onRetry }) {
  const user = useAuthStore((state) => state.user);
  const [showActions, setShowActions] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const swipeTriggered = useRef(false);

  const senderId = String(message.sender?._id || message.sender?.id || '');
  const currentUserId = String(user?._id || user?.id || '');
  const isMyMessage = senderId === currentUserId && senderId !== '';
  // Not saved yet (pending) or failed: no real id, so it can't be replied to
  const isUnsent = Boolean(message.pending || message.failed);

  // WhatsApp-style clock time: "4:55 PM", "Yesterday 4:55 PM", "12 Oct, 4:55 PM"
  const formatTime = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    if (isToday(d)) return format(d, 'h:mm a');
    if (isYesterday(d)) return `Yesterday ${format(d, 'h:mm a')}`;
    return format(d, 'd MMM, h:mm a');
  };

  const handleTouchStart = useCallback((e) => {
    if (isUnsent) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    swipeTriggered.current = false;
    setSwiping(true);
  }, [isUnsent]);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;

    if (Math.abs(dy) > Math.abs(dx)) {
      setSwipeX(0);
      return;
    }

    // Own messages: swipe left (negative dx), others: swipe right (positive dx)
    let swipeVal = 0;
    if (isMyMessage) {
      swipeVal = Math.max(0, Math.min(-dx, SWIPE_THRESHOLD + 10));
    } else {
      swipeVal = Math.max(0, Math.min(dx, SWIPE_THRESHOLD + 10));
    }
    setSwipeX(swipeVal);

    if (swipeVal >= SWIPE_THRESHOLD && !swipeTriggered.current) {
      swipeTriggered.current = true;
      if (navigator.vibrate) navigator.vibrate(30);
    }
  }, [isMyMessage]);

  const handleTouchEnd = useCallback(() => {
    if (swipeTriggered.current) {
      onReply(message);
    }
    setSwipeX(0);
    setSwiping(false);
    touchStartX.current = null;
    touchStartY.current = null;
    swipeTriggered.current = false;
  }, [message, onReply]);

  const renderAttachment = (attachment) => {
    const { url, filename, fileType } = attachment;

    if (fileType?.startsWith('image/')) {
      return (
        <div className="image-message-wrapper">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img
              src={url}
              alt={filename || "Image attachment"}
              className="message-image"
              loading="lazy"
              onError={(e) => { e.target.alt = "Failed to load image"; }}
            />
          </a>
        </div>
      );
    }

    if (fileType?.startsWith('audio/')) {
      return (
        <div className="audio-message">
          <FiMusic />
          <audio controls src={url} className="audio-player" />
        </div>
      );
    }

    if (fileType?.startsWith('video/')) {
      return <video controls src={url} className="message-video" />;
    }

    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="file-attachment">
        <div className="file-icon"><FiFile /></div>
        <div className="file-info">
          <div className="file-name">{filename}</div>
          <FiDownload className="download-icon" />
        </div>
      </a>
    );
  };

  const swipeProgress = Math.min(swipeX / SWIPE_THRESHOLD, 1);

  return (
    <div
      className={`message ${isMyMessage ? 'own-message' : 'other-message'}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: swipeX > 0
          ? `translateX(${isMyMessage ? -swipeX : swipeX}px)`
          : undefined,
        transition: swiping ? 'none' : 'transform 0.2s ease',
      }}
    >
      {swipeX > 8 && (
        <div
          className="swipe-reply-icon"
          style={{
            opacity: swipeProgress,
            transform: isMyMessage
              ? `translateX(${swipeX - 4}px) scale(${0.6 + swipeProgress * 0.4})`
              : `translateX(${-swipeX + 4}px) scale(${0.6 + swipeProgress * 0.4})`,
            [isMyMessage ? 'right' : 'left']: 0,
          }}
        >
          <FiCornerUpLeft />
        </div>
      )}

      {!isMyMessage && showAvatar && (
        message.sender?.profilePicture ? (
          <img src={message.sender.profilePicture} alt="Profile" className="message-avatar message-avatar-img" />
        ) : (
          <div className="message-avatar">
            {message.sender?.firstName?.[0]}{message.sender?.lastName?.[0]}
          </div>
        )
      )}
      {!isMyMessage && !showAvatar && <div className="message-avatar-spacer" />}

      <div className="message-content">
        {!isMyMessage && showAvatar && (
          <div className="message-header">
            <span className="message-sender">
              {message.sender?.firstName} {message.sender?.lastName}
            </span>
            <span className="message-time">{formatTime(message.createdAt)}</span>
          </div>
        )}
        <div className="message-bubble">
          {message.replyTo && (
            <div className="message-reply-preview">
              <div className="reply-border"></div>
              <div className="reply-content">
                <div className="reply-sender">
                  {message.replyTo.sender?.firstName} {message.replyTo.sender?.lastName}
                </div>
                <div className="reply-text">
                  {message.replyTo.content || 'Attachment'}
                </div>
              </div>
            </div>
          )}
          {message.attachments && message.attachments.length > 0 && (
  <div className="message-attachments">
    {message.attachments.map((attachment, idx) => (
      <div key={idx}>{renderAttachment(attachment)}</div>
    ))}
  </div>
)}

{message.content &&
  !/^voice-\d+\.webm$/i.test(message.content.trim()) && (
    <div className="message-text">{message.content}</div>
  )}

        </div>
        {message.failed ? (
          <button type="button" className="message-failed" onClick={() => onRetry?.(message._id)}>
            <FiAlertCircle /> Not sent · Tap to retry
          </button>
        ) : (isOwn || message.editedAt) && (
          <div className={isOwn ? "message-time-own" : "message-time"}>
            {formatTime(message.createdAt)}
            {message.editedAt && <span className="edited-tag">(edited)</span>}
            {isOwn && (
              <span className={`message-status ${message.pending ? 'pending' : 'sent'}`} title={message.pending ? 'Sending...' : 'Sent'}>
                {message.pending ? <FiClock /> : <FiCheck />}
              </span>
            )}
          </div>
        )}
      </div>

      {showActions && !isUnsent && (
        <button
          className="message-reply-btn"
          onClick={() => onReply(message)}
          title="Reply to message"
        >
          <FiCornerUpLeft />
        </button>
      )}
    </div>
  );
}

export default React.memo(Message);
